import {
    createFileRoute,
    Outlet,
    useNavigate,
    useRouterState,
} from '@tanstack/react-router';

import { LIST_PAGE_FRAME_CLASSNAME } from '@apps/web/components/layout/page_layout';
import { ListPageHeader } from '@apps/web/components/ui/list_page_header';
import { ListPageTabs } from '@apps/web/components/ui/list_page_tabs';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { useFeatureFlag } from '@apps/web/lib/feature_flag_overrides';
import { cn } from '@apps/web/lib/utils';
import { useOrganization } from '@apps/web/swr_data_fetching/organization_queries';

export const Route = createFileRoute('/_app/(main)/training')({
    component: TrainingLayout,
    head: () => ({
        meta: [{ title: 'Staff Training | Phoebe' }],
    }),
});

const TRAINING_TABS = [
    {
        id: 'announcement',
        label: 'Announce Phoebe',
        to: '/training/announcement',
    },
    {
        id: 'onboarding',
        label: 'Onboard Caregivers',
        to: '/training/onboarding',
    },
    {
        id: 'client-onboarding',
        label: 'Onboard Clients',
        to: '/training/client-onboarding',
    },
    { id: 'demo', label: 'Demo Page', to: '/training/demo' },
] as const;

function TrainingLayout() {
    const navigate = useNavigate();
    const pathname = useRouterState({
        select: (state) => state.location.pathname,
    });

    const organizationId = useOrganizationId();
    const { data: organization } = useOrganization(organizationId, {
        reason: 'training-tabs',
    });
    const clientAgent = useFeatureFlag(
        'client_agent_allowed',
        organization?.features,
    );
    const tabs = TRAINING_TABS.filter(
        (tab) => tab.id !== 'client-onboarding' || clientAgent,
    );
    const activeTab =
        TRAINING_TABS.find((tab) => pathname.endsWith(tab.to))?.id ??
        'announcement';
    const handleTabChange = (value: string) => {
        const tab = TRAINING_TABS.find((item) => item.id === value);
        void navigate({
            to: tab?.to ?? '/training/announcement',
            replace: true,
        });
    };

    return (
        <div
            className={cn(
                'flex h-full min-w-0 flex-col overflow-hidden',
                LIST_PAGE_FRAME_CLASSNAME,
                'mx-auto w-full gap-5',
            )}
        >
            {/* Header */}
            <ListPageHeader
                title="Staff Training"
                description="Resources to help staff work effectively with Phoebe"
                hideTitleOnPhone={true}
            />
            {/* Content Area */}
            <div className="min-w-0 flex-1 overflow-y-auto">
                <div className="mx-auto w-full min-w-0 space-y-6">
                    <ListPageTabs
                        aria-label="Staff training resources"
                        tabs={tabs}
                        activeTab={activeTab}
                        onTabChange={handleTabChange}
                    />
                    <Outlet />
                </div>
            </div>
        </div>
    );
}
